import { Icon, IconButton, type IconButtonProps } from '@chakra-ui/react'
import { type ConditionalValue } from '@chakra-ui/react'
import { PhoneCall, PhoneOff } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '~/components/ui/tooltip'
import { Size } from '~/types/ThemeTypes'

import {
  CallButtonVariants,
  useCallButtonStyles,
} from './componentStyles/CallButton.style'

type CallButtonProps = {
  variant: CallButtonVariants
  size?: ConditionalValue<Size.sm | Size.md | Size.lg | Size.full>
  disabled?: boolean
  onClick?: () => void
} & Omit<IconButtonProps, 'aria-label' | 'onClick'>
export const CallButton = ({
  variant,
  size,
  disabled,
  onClick,
  ...rest
}: CallButtonProps) => {
  const { t } = useTranslation()
  const styles = useCallButtonStyles({
    variant: disabled ? CallButtonVariants.Disabled : variant,
    size,
  })

  const getTooltipText = () => {
    switch (variant) {
      case CallButtonVariants.Start:
        return t('callButton.startCall')
      case CallButtonVariants.Stop:
        return t('callButton.endCall')
      case CallButtonVariants.Disabled:
        return t('callButton.disabled')
      default:
        return t('callButton.unknown')
    }
  }
  const getIcon = () => {
    switch (variant) {
      case CallButtonVariants.Start:
        return (
          <Icon {...styles.icon}>
            <PhoneCall size="100%" />
          </Icon>
        )
      case CallButtonVariants.Stop:
        return (
          <Icon {...styles.icon}>
            <PhoneOff size="100%" />
          </Icon>
        )
    }
  }

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick()
    }
  }

  return (
    <Tooltip
      showArrow
      content={getTooltipText()}
      contentProps={{ display: 'none' }}
    >
      <IconButton
        {...styles.button}
        onClick={handleClick}
        aria-label={getTooltipText()}
        flexShrink="0"
        {...rest}
      >
        {getIcon()}
      </IconButton>
    </Tooltip>
  )
}
